// AdminUsers — User management w/ search, filter, full action set
// Per-user actions: edit, ban, block, send message, upgrade tier, view detail, add balance, deduct

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TierPill } from '../../components/Brand';

interface User {
  id: string; name: string; email: string; tier: 'Beginner' | 'Mid' | 'Professional';
  status: 'active' | 'suspended' | 'banned' | 'pending' | 'blocked';
  balance: number; pnl30d: number; trades: number; kyc: 'verified' | 'pending' | 'rejected' | 'unverified';
  country: string; joined: string; lastActive: string; flagged?: boolean;
}

const MOCK_USERS: User[] = Array.from({ length: 32 }, (_, i) => {
  const names = ['Brandon Lee', 'Sofia Petrov', 'Marcus Chen', 'Aiden Wright', 'Lena Voss', 'Yuki Tanaka', 'Diego Rivera', 'Aisha Khan', 'Ravi Patel', 'Mia Johansson', 'James O\'Brien', 'Priya Sharma', 'Carlos Mendoza', 'Emma Wilson', 'Hiroshi Tanaka', 'Anya Volkov', 'Tom Bradley', 'Zoe Carter', 'Ravi Singh', 'Nina Petrov', 'Leo Marquez', 'Hannah Kim', 'Kai Nakamura', 'Olivia Brown', 'Felix Becker', 'Lina Hassan', 'Marco Rossi', 'Stella Green', 'Pablo Diaz', 'Yuna Park', 'Theo Lambert', 'Iris Wong'];
  const tiers: User['tier'][] = ['Beginner', 'Mid', 'Professional'];
  const statuses: User['status'][] = ['active', 'active', 'active', 'active', 'suspended', 'banned', 'pending', 'blocked'];
  const countries = ['US', 'GB', 'CA', 'DE', 'FR', 'JP', 'AU', 'SG', 'IN', 'BR'];
  const kycs: User['kyc'][] = ['verified', 'verified', 'pending', 'rejected', 'unverified'];
  const n = names[i % names.length];
  return {
    id: `u${i + 1}`,
    name: n, email: `${n.toLowerCase().replace(/[^a-z]/g, '')}@email.com`,
    tier: tiers[i % 3], status: i < 28 ? 'active' : statuses[i % statuses.length],
    balance: 1000 + Math.random() * 50000,
    pnl30d: (Math.random() - 0.3) * 5000,
    trades: Math.floor(Math.random() * 200),
    kyc: kycs[i % 5], country: countries[i % 10],
    joined: `2026-${String((i % 6) + 1).padStart(2, '0')}-${String((i % 28) + 1).padStart(2, '0')}`,
    lastActive: i === 0 ? 'now' : i < 5 ? `${i * 12}m ago` : `${i}h ago`,
    flagged: i === 7 || i === 14,
  };
});

const STATUS_META = {
  active:    { color: '#34C759', label: 'Active' },
  suspended: { color: '#FF9500', label: 'Suspended' },
  banned:    { color: '#FF3B30', label: 'Banned' },
  pending:   { color: '#5856D6', label: 'Pending' },
  blocked:   { color: '#1C1C1E', label: 'Blocked' },
};

const KYC_META = {
  verified:    { color: '#34C759', label: 'Verified' },
  pending:     { color: '#FF9500', label: 'Pending' },
  rejected:    { color: '#FF3B30', label: 'Rejected' },
  unverified:  { color: '#8E8E93', label: 'Unverified' },
};

export function AdminUsers() {
  const nav = useNavigate();
  const [users, setUsers] = useState(MOCK_USERS);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'flagged' | 'banned' | 'suspended' | 'pending' | 'active' | 'blocked'>('all');
  const [tierFilter, setTierFilter] = useState<'all' | 'Beginner' | 'Mid' | 'Professional'>('all');
  const [selected, setSelected] = useState<string[]>([]);
  const [actionTarget, setActionTarget] = useState<User | null>(null);
  const [actionType, setActionType] = useState<'message' | 'balance' | 'deduct' | 'tier' | 'ban' | 'suspend' | 'unban' | 'block' | 'unblock' | null>(null);

  const filtered = users.filter((u) => {
    if (search && !`${u.name} ${u.email} ${u.id}`.toLowerCase().includes(search.toLowerCase())) return false;
    if (filter === 'flagged' && !u.flagged) return false;
    if (filter !== 'all' && filter !== 'flagged' && u.status !== filter) return false;
    if (tierFilter !== 'all' && u.tier !== tierFilter) return false;
    return true;
  });

  function performAction() {
    if (!actionTarget || !actionType) return;
    setUsers((curr) => curr.map((u) => {
      if (u.id !== actionTarget.id) return u;
      if (actionType === 'ban')       return { ...u, status: 'banned' };
      if (actionType === 'suspend')   return { ...u, status: 'suspended' };
      if (actionType === 'unban')     return { ...u, status: 'active' };
      if (actionType === 'block')     return { ...u, status: 'blocked' };
      if (actionType === 'unblock')   return { ...u, status: 'active' };
      if (actionType === 'tier')      return { ...u, tier: (document.getElementById('admin-tier-pick') as HTMLSelectElement)?.value as any || u.tier };
      return u;
    }));
    setActionTarget(null);
    setActionType(null);
    alert(`Action "${actionType}" applied to ${actionTarget.name}`);
  }

  function bulkAction(action: string) {
    if (selected.length === 0) { alert('Select users first'); return; }
    if (!confirm(`${action} ${selected.length} user(s)?`)) return;
    setUsers((curr) => curr.map((u) => {
      if (!selected.includes(u.id)) return u;
      if (action === 'Approve KYC') return { ...u, kyc: 'verified' };
      if (action === 'Ban') return { ...u, status: 'banned' };
      if (action === 'Send message') return u;
      return u;
    }));
    alert(`${action} applied to ${selected.length} user(s)`);
    setSelected([]);
  }

  return (
    <div className="space-y-4 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-[22px] font-bold text-white">Users</h1>
          <p className="text-[12px] text-white/50">{filtered.length.toLocaleString()} of {users.length.toLocaleString()} users</p>
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={() => alert('Export CSV')} data-testid="export-users" className="h-9 rounded-[8px] bg-white/5 border border-white/10 px-3 text-[12px] font-semibold text-white/80 active:bg-white/10">Export</button>
          <button onClick={() => bulkAction('Send message')} data-testid="bulk-message" disabled={selected.length === 0} className="h-9 rounded-[8px] bg-[#FF9500] px-3 text-[12px] font-semibold text-white active:opacity-40 disabled:opacity-30">Broadcast ({selected.length})</button>
        </div>
      </div>

      {/* Search + filters */}
      <div className="rounded-[16px] bg-[#1C1C1E] border border-white/5 p-3 space-y-2">
        <div className="relative">
          <svg viewBox="0 0 24 24" className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" strokeLinecap="round" /></svg>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, email, ID..." data-testid="users-search" className="h-10 w-full rounded-[8px] bg-black/40 border border-white/10 pl-9 pr-3 text-[13px] text-white placeholder:text-white/30 focus:outline-none focus:border-[#FF3B30]" />
        </div>
        <div className="flex items-center gap-1.5 overflow-x-auto">
          {(['all', 'active', 'flagged', 'pending', 'suspended', 'banned', 'blocked'] as const).map((f) => (
            <button
              key={f} onClick={() => setFilter(f)}
              data-testid={`filter-${f}`}
              className={['shrink-0 rounded-full px-3 py-1 text-[11px] font-semibold capitalize transition',
                filter === f ? 'bg-white text-black' : 'bg-white/5 text-white/60 active:bg-white/10'
              ].join(' ')}
            >
              {f}
              {f !== 'all' && <span className="ml-1 text-[10px] opacity-70">{users.filter((u) => f === 'flagged' ? u.flagged : u.status === f).length}</span>}
            </button>
          ))}
          <div className="mx-1 h-4 w-px bg-white/10" />
          {(['all', 'Beginner', 'Mid', 'Professional'] as const).map((t) => (
            <button
              key={t} onClick={() => setTierFilter(t)}
              data-testid={`tier-filter-${t}`}
              className={['shrink-0 rounded-full px-3 py-1 text-[11px] font-semibold transition',
                tierFilter === t ? 'bg-white text-black' : 'bg-white/5 text-white/60 active:bg-white/10'
              ].join(' ')}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* Bulk actions bar (when selected) */}
      {selected.length > 0 && (
        <div className="rounded-[12px] bg-[#FF9500]/10 border border-[#FF9500]/30 px-3 py-2 flex items-center gap-2" data-testid="bulk-bar">
          <div className="text-[12px] font-semibold text-[#FF9500]">{selected.length} selected</div>
          <div className="flex-1" />
          <button onClick={() => bulkAction('Approve KYC')} data-testid="bulk-approve-kyc" className="rounded-full bg-[#34C759] px-3 py-1 text-[11px] font-semibold text-white">Approve KYC</button>
          <button onClick={() => bulkAction('Ban')} data-testid="bulk-ban" className="rounded-full bg-[#FF3B30] px-3 py-1 text-[11px] font-semibold text-white">Ban</button>
          <button onClick={() => setSelected([])} className="text-[11px] text-white/60">Clear</button>
        </div>
      )}

      {/* Users table */}
      <div className="rounded-[16px] bg-[#1C1C1E] border border-white/5 overflow-hidden">
        {/* Table header */}
        <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-2.5 border-b border-white/5 bg-white/[0.02] text-[10px] font-bold uppercase tracking-wider text-white/40">
          <div className="col-span-1 flex items-center">
            <input type="checkbox" checked={selected.length === filtered.length && filtered.length > 0} onChange={(e) => setSelected(e.target.checked ? filtered.map((u) => u.id) : [])} data-testid="select-all" className="accent-[#FF3B30]" />
          </div>
          <div className="col-span-3">User</div>
          <div className="col-span-1">Tier</div>
          <div className="col-span-1">KYC</div>
          <div className="col-span-1">Status</div>
          <div className="col-span-1">Country</div>
          <div className="col-span-1 text-right">Balance</div>
          <div className="col-span-1 text-right">P&L (30d)</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {/* Rows */}
        <div className="divide-y divide-white/5">
          {filtered.slice(0, 25).map((u) => {
            const s = STATUS_META[u.status];
            const k = KYC_META[u.kyc];
            return (
              <div key={u.id} data-testid={`user-row-${u.id}`} className="grid grid-cols-12 gap-2 px-4 py-3 items-center hover:bg-white/[0.02]">
                <div className="col-span-1">
                  <input type="checkbox" checked={selected.includes(u.id)} onChange={(e) => setSelected((curr) => e.target.checked ? [...curr, u.id] : curr.filter((x) => x !== u.id))} data-testid={`select-${u.id}`} className="accent-[#FF3B30]" />
                </div>
                <div className="col-span-3 flex items-center gap-2 min-w-0">
                  {u.flagged && <span className="text-[10px]" title="Flagged">🚩</span>}
                  <div className="min-w-0">
                    <div className="text-[12px] font-semibold text-white truncate">{u.name}</div>
                    <div className="text-[10px] text-white/40 truncate">{u.email} · {u.id}</div>
                  </div>
                </div>
                <div className="col-span-1"><TierPill tier={u.tier} /></div>
                <div className="col-span-1"><span className="rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase" style={{ backgroundColor: k.color + '20', color: k.color }}>{k.label}</span></div>
                <div className="col-span-1"><span className="rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase" style={{ backgroundColor: s.color + '20', color: s.color }}>{s.label}</span></div>
                <div className="col-span-1 text-[11px] text-white/60">{u.country}</div>
                <div className="col-span-1 text-right text-[12px] font-semibold text-white tabular-nums">${u.balance.toFixed(0)}</div>
                <div className={['col-span-1 text-right text-[12px] font-semibold tabular-nums', u.pnl30d >= 0 ? 'text-[#34C759]' : 'text-[#FF3B30]'].join(' ')}>{u.pnl30d >= 0 ? '+' : ''}${u.pnl30d.toFixed(0)}</div>
                <div className="col-span-2 flex items-center justify-end gap-1">
                  <button onClick={() => nav(`/admin/users/${u.id}`)} data-testid={`view-${u.id}`} className="h-7 rounded-full bg-white/5 px-2 text-[10px] font-semibold text-white/80 active:bg-white/10">View</button>
                  <button onClick={() => { setActionTarget(u); setActionType('message'); }} className="h-7 rounded-full bg-[#FF9500]/20 px-2 text-[10px] font-semibold text-[#FF9500] active:bg-[#FF9500]/30">Msg</button>
                  <button onClick={() => { setActionTarget(u); setActionType('tier'); }} className="h-7 rounded-full bg-[#007AFF]/20 px-2 text-[10px] font-semibold text-[#007AFF] active:bg-[#007AFF]/30">Tier</button>
                  {u.status === 'active' ? (
                    <button onClick={() => { setActionTarget(u); setActionType('ban'); }} data-testid={`ban-${u.id}`} className="h-7 rounded-full bg-[#FF3B30]/20 px-2 text-[10px] font-semibold text-[#FF3B30] active:bg-[#FF3B30]/30">Ban</button>
                  ) : u.status === 'banned' || u.status === 'suspended' || u.status === 'blocked' ? (
                    <button onClick={() => { setActionTarget(u); setActionType('unban'); }} data-testid={`unban-${u.id}`} className="h-7 rounded-full bg-[#34C759]/20 px-2 text-[10px] font-semibold text-[#34C759] active:bg-[#34C759]/30">Unban</button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
        <div className="border-t border-white/5 px-4 py-2.5 flex items-center justify-between text-[11px] text-white/40">
          <div>Showing {Math.min(25, filtered.length)} of {filtered.length}</div>
          <div className="flex items-center gap-1.5">
            <button className="h-7 rounded px-2 bg-white/5 text-white/60 active:bg-white/10">‹</button>
            <span>1 of 2</span>
            <button className="h-7 rounded px-2 bg-white/5 text-white/60 active:bg-white/10">›</button>
          </div>
        </div>
      </div>

      {/* Action modal */}
      {actionTarget && actionType && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => { setActionTarget(null); setActionType(null); }}>
          <div className="w-full max-w-md rounded-[20px] bg-[#1C1C1E] border border-white/10" onClick={(e) => e.stopPropagation()}>
            <div className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-[10px] bg-gradient-to-br from-[#FF3B30] to-[#FF9500] text-white text-[16px]">🛡️</div>
                <div>
                  <h2 className="text-[16px] font-bold text-white capitalize">{actionType} user</h2>
                  <p className="text-[11px] text-white/50">{actionTarget.name} · {actionTarget.email}</p>
                </div>
              </div>

              {actionType === 'message' && (
                <div className="space-y-2.5">
                  <div>
                    <label className="block text-[10px] font-semibold uppercase tracking-wider text-white/50 mb-1.5">Subject</label>
                    <input placeholder="Account verification" data-testid="action-subject" className="w-full h-10 rounded-[8px] bg-black/40 border border-white/10 px-3 text-[13px] text-white focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-semibold uppercase tracking-wider text-white/50 mb-1.5">Message</label>
                    <textarea rows={4} placeholder="Hi..." data-testid="action-message" className="w-full rounded-[8px] bg-black/40 border border-white/10 p-3 text-[13px] text-white focus:outline-none resize-none" />
                  </div>
                  <label className="flex items-center gap-2 text-[12px] text-white/70">
                    <input type="checkbox" defaultChecked className="accent-[#FF3B30]" />
                    Send via email + in-app notification
                  </label>
                </div>
              )}

              {actionType === 'tier' && (
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-wider text-white/50 mb-1.5">New tier</label>
                  <select id="admin-tier-pick" data-testid="action-tier" defaultValue={actionTarget.tier} className="w-full h-10 rounded-[8px] bg-black/40 border border-white/10 px-3 text-[13px] text-white focus:outline-none">
                    <option value="Beginner">Beginner ($50K)</option>
                    <option value="Mid">Mid ($100K)</option>
                    <option value="Professional">Professional ($150K)</option>
                  </select>
                  <p className="mt-2 text-[11px] text-white/50">User will be notified of tier change. Active challenges remain at their current size.</p>
                </div>
              )}

              {(actionType === 'balance' || actionType === 'deduct') && (
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-wider text-white/50 mb-1.5">Amount (USD)</label>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[16px] text-white/50">$</span>
                    <input type="number" placeholder="100.00" data-testid="action-amount" className="flex-1 h-10 rounded-[8px] bg-black/40 border border-white/10 px-3 text-[13px] text-white focus:outline-none" />
                  </div>
                  <div className="mt-2">
                    <label className="block text-[10px] font-semibold uppercase tracking-wider text-white/50 mb-1.5">Reason</label>
                    <select className="w-full h-10 rounded-[8px] bg-black/40 border border-white/10 px-3 text-[13px] text-white focus:outline-none">
                      <option>Promo bonus</option>
                      <option>Compensation</option>
                      <option>Refund</option>
                      <option>Manual adjustment</option>
                    </select>
                  </div>
                </div>
              )}

              {(actionType === 'ban' || actionType === 'suspend' || actionType === 'unban' || actionType === 'block' || actionType === 'unblock') && (
                <div className="space-y-2">
                  <div>
                    <label className="block text-[10px] font-semibold uppercase tracking-wider text-white/50 mb-1.5">Reason</label>
                    <select className="w-full h-10 rounded-[8px] bg-black/40 border border-white/10 px-3 text-[13px] text-white focus:outline-none">
                      <option>Violation of ToS</option>
                      <option>Suspicious activity</option>
                      <option>Chargeback / fraud</option>
                      <option>Identity verification failed</option>
                      <option>Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] font-semibold uppercase tracking-wider text-white/50 mb-1.5">Notes (audit log)</label>
                    <textarea rows={3} placeholder="Explain reason..." data-testid="action-notes" className="w-full rounded-[8px] bg-black/40 border border-white/10 p-3 text-[13px] text-white focus:outline-none resize-none" />
                  </div>
                  <label className="flex items-center gap-2 text-[12px] text-white/70">
                    <input type="checkbox" defaultChecked className="accent-[#FF3B30]" />
                    Notify user via email
                  </label>
                </div>
              )}

              <div className="mt-4 flex gap-2">
                <button onClick={() => { setActionTarget(null); setActionType(null); }} className="flex-1 h-11 rounded-full bg-white/5 text-[14px] font-semibold text-white active:bg-white/10">Cancel</button>
                <button onClick={performAction} data-testid="confirm-action" className="flex-1 h-11 rounded-full bg-gradient-to-r from-[#FF3B30] to-[#FF9500] text-[14px] font-bold text-white active:opacity-60">
                  Confirm {actionType}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
